/**
 * Quiz Play Screen
 * The actual quiz gameplay with timer, questions, and answers
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Animated,
  Dimensions,
  Alert,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useQuiz, SubmitAnswerResult, QuizSessionSetup } from "@/modules/quiz";
import { SessionMode } from "@/modules/games";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

export default function QuizPlayScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    quizId?: string;
    categoryId?: string;
    difficulty?: string;
    count?: string;
    mode?: string;
  }>();
  const { isDark } = useTheme();
  const colors = useThemeColors();

  const mode: SessionMode = params.mode === "weekend" ? "weekend" : "free";

  const {
    currentQuestion,
    currentIndex,
    totalQuestions,
    score,
    correctCount,
    timeRemaining,
    isPlaying,
    isLoading,
    isSubmitting,
    lastAnswerResult,
    error,
    startQuiz,
    submitAnswer,
    nextQuestion,
    endQuiz,
  } = useQuiz({ mode });

  // Track if we've started the quiz
  const [hasStarted, setHasStarted] = useState(false);

  /**
   * Start the quiz when screen loads
   */
  useEffect(() => {
    if (hasStarted) return;

    const initQuiz = async () => {
      const setup: QuizSessionSetup = {};

      if (params.quizId) {
        setup.quiz_id = params.quizId;
      }
      if (params.categoryId && params.categoryId !== "all") {
        setup.category_id = params.categoryId;
      }
      if (params.difficulty) {
        setup.difficulty = params.difficulty as any;
      }
      if (params.count) {
        setup.question_count = parseInt(params.count, 10);
      }

      setHasStarted(true);
      await startQuiz(setup, mode);
    };

    initQuiz();
  }, [params, hasStarted, startQuiz, mode]);

  // State for this screen
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [answerStartTime, setAnswerStartTime] = useState<number>(Date.now());
  const [showResult, setShowResult] = useState(false);

  // Animation values
  const progressAnim = useRef(new Animated.Value(1)).current;
  const questionAnim = useRef(new Animated.Value(0)).current;
  const resultAnim = useRef(new Animated.Value(0)).current;

  // Calculate timer progress
  const timerProgress =
    currentQuestion && currentQuestion.time_limit_seconds > 0
      ? timeRemaining / currentQuestion.time_limit_seconds
      : 1;

  /**
   * Handle option selection
   */
  const handleSelectOption = async (optionIndex: number) => {
    if (selectedOption !== null || isSubmitting || !isPlaying) {
      return;
    }

    setSelectedOption(optionIndex);
    const timeTaken = Date.now() - answerStartTime;

    const result = await submitAnswer(optionIndex, timeTaken);

    if (result) {
      setShowResult(true);
      // Animate result feedback
      Animated.spring(resultAnim, {
        toValue: 1,
        useNativeDriver: true,
        tension: 80,
        friction: 10,
      }).start();
    }
  };

  /**
   * Handle next question
   */
  const handleNextQuestion = () => {
    setShowResult(false);
    setSelectedOption(null);
    resultAnim.setValue(0);

    // Animate question transition
    Animated.sequence([
      Animated.timing(questionAnim, {
        toValue: -1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(questionAnim, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }),
    ]).start();

    setAnswerStartTime(Date.now());
    nextQuestion();
  };

  /**
   * Handle quit quiz
   */
  const handleQuitQuiz = () => {
    Alert.alert(
      "Quiz Stoppen?",
      "Weet je zeker dat je de quiz wilt stoppen? Je voortgang wordt opgeslagen.",
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Stoppen",
          style: "destructive",
          onPress: async () => {
            const result = await endQuiz();
            if (result) {
              router.replace({
                pathname: "/(tabs)/games/quiz/result",
                params: { 
                  result: JSON.stringify(result), 
                  mode,
                  quizId: params.quizId || "",
                },
              });
            }
          },
        },
      ]
    );
  };

  // Redirect to result when quiz is not playing (natural end)
  useEffect(() => {
    if (!isPlaying && totalQuestions > 0 && hasStarted) {
      // Quiz ended, get result and navigate
      const navigateToResult = async () => {
        const result = await endQuiz();
        if (result) {
          router.replace({
            pathname: "/(tabs)/games/quiz/result",
            params: { 
              result: JSON.stringify(result), 
              mode,
              quizId: params.quizId || "",
            },
          });
        }
      };
      navigateToResult();
    }
  }, [isPlaying, totalQuestions, hasStarted, endQuiz, router, mode, params.quizId]);

  // Reset answer start time on question change
  useEffect(() => {
    setAnswerStartTime(Date.now());
    setSelectedOption(null);
    setShowResult(false);
  }, [currentIndex]);

  // Update timer progress animation
  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: timerProgress,
      duration: 1000,
      useNativeDriver: false,
    }).start();
  }, [timerProgress, progressAnim]);

  // Show loading state
  if (isLoading || !currentQuestion) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-4" style={{ color: colors.textSecondary }}>
          Quiz wordt geladen...
        </Text>
        {error && (
          <View className="mt-4 px-6">
            <Text className="text-center" style={{ color: colors.error }}>
              {error}
            </Text>
            <TouchableOpacity
              onPress={() => router.back()}
              className="mt-4 px-6 py-3 rounded-xl"
              style={{ backgroundColor: colors.primary }}
            >
              <Text className="text-white font-medium text-center">Terug</Text>
            </TouchableOpacity>
          </View>
        )}
      </SafeAreaView>
    );
  }

  /**
   * Get option style based on state
   */
  const getOptionStyle = (index: number) => {
    if (!showResult) {
      return {
        backgroundColor: selectedOption === index
          ? `${colors.primary}20`
          : colors.surface,
        borderColor: selectedOption === index
          ? colors.primary
          : colors.border,
      };
    }

    const isCorrect = currentQuestion.options[index]?.is_correct;
    const wasSelected = selectedOption === index;

    if (isCorrect) {
      return {
        backgroundColor: "#10B98130",
        borderColor: "#10B981",
      };
    }

    if (wasSelected && !isCorrect) {
      return {
        backgroundColor: "#EF444430",
        borderColor: "#EF4444",
      };
    }

    return {
      backgroundColor: colors.surface,
      borderColor: colors.border,
      opacity: 0.5,
    };
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      {/* Header */}
      <View className="px-4 py-3 flex-row items-center justify-between">
        <TouchableOpacity
          onPress={handleQuitQuiz}
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: colors.surface }}
          accessibilityRole="button"
          accessibilityLabel="Stop quiz"
        >
          <Ionicons name="close" size={22} color={colors.text} />
        </TouchableOpacity>

        <View className="flex-1 mx-4">
          <View className="flex-row items-center justify-center">
            <Text className="text-sm font-medium" style={{ color: colors.textSecondary }}>
              Vraag {currentIndex + 1} van {totalQuestions}
            </Text>
          </View>
          {/* Progress Bar */}
          <View
            className="h-2 rounded-full mt-2 overflow-hidden"
            style={{ backgroundColor: colors.border }}
          >
            <View
              className="h-full rounded-full"
              style={{
                backgroundColor: colors.primary,
                width: `${((currentIndex + (showResult ? 1 : 0)) / totalQuestions) * 100}%`,
              }}
            />
          </View>
        </View>

        <View className="items-center">
          <Text className="text-lg font-bold" style={{ color: colors.primary }}>
            {score}
          </Text>
          <Text className="text-xs" style={{ color: colors.textSecondary }}>
            punten
          </Text>
        </View>
      </View>

      {/* Timer */}
      <View className="mx-4 mt-2">
        <View
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: colors.border }}
        >
          <Animated.View
            className="h-full rounded-full"
            style={{
              backgroundColor:
                timerProgress > 0.5
                  ? "#10B981"
                  : timerProgress > 0.25
                    ? "#F59E0B"
                    : "#EF4444",
              width: progressAnim.interpolate({
                inputRange: [0, 1],
                outputRange: ["0%", "100%"],
              }),
            }}
          />
        </View>
        <View className="flex-row items-center justify-center mt-2">
          <Ionicons
            name="time"
            size={16}
            color={
              timerProgress > 0.25 ? colors.textSecondary : "#EF4444"
            }
          />
          <Text
            className="ml-1 font-medium"
            style={{
              color: timerProgress > 0.25 ? colors.textSecondary : "#EF4444",
            }}
          >
            {timeRemaining}s
          </Text>
        </View>
      </View>

      {/* Question */}
      <Animated.View
        className="flex-1 px-4 mt-6"
        style={{
          transform: [
            {
              translateX: questionAnim.interpolate({
                inputRange: [-1, 0, 1],
                outputRange: [-SCREEN_WIDTH, 0, SCREEN_WIDTH],
              }),
            },
          ],
        }}
      >
        {/* Category Badge */}
        {currentQuestion.category && (
          <View className="flex-row mb-3">
            <View
              className="px-3 py-1 rounded-full"
              style={{
                backgroundColor: (currentQuestion.category as any)?.color
                  ? `${(currentQuestion.category as any).color}20`
                  : colors.primaryLight,
              }}
            >
              <Text
                className="text-xs font-medium"
                style={{
                  color: (currentQuestion.category as any)?.color || colors.primary,
                }}
              >
                {(currentQuestion.category as any)?.name || "Algemeen"}
              </Text>
            </View>
          </View>
        )}

        {/* Question Text */}
        <Text
          className="text-xl font-bold leading-8 mb-6"
          style={{ color: colors.text }}
        >
          {currentQuestion.question}
        </Text>

        {/* Options */}
        <View>
          {currentQuestion.options.map((option, index) => (
            <TouchableOpacity
              key={index}
              onPress={() => handleSelectOption(index)}
              disabled={showResult || isSubmitting}
              className="rounded-xl p-4 mb-3 flex-row items-center"
              style={{
                ...getOptionStyle(index),
                borderWidth: 2,
              }}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={`Optie ${index + 1}: ${option.text}`}
            >
              <View
                className="w-8 h-8 rounded-full items-center justify-center mr-3"
                style={{
                  backgroundColor: showResult
                    ? option.is_correct
                      ? "#10B98130"
                      : selectedOption === index
                        ? "#EF444430"
                        : colors.surfaceSecondary
                    : colors.surfaceSecondary,
                }}
              >
                {showResult && option.is_correct ? (
                  <Ionicons name="checkmark" size={18} color="#10B981" />
                ) : showResult && selectedOption === index && !option.is_correct ? (
                  <Ionicons name="close" size={18} color="#EF4444" />
                ) : (
                  <Text
                    className="font-bold"
                    style={{ color: colors.textSecondary }}
                  >
                    {String.fromCharCode(65 + index)}
                  </Text>
                )}
              </View>

              <Text
                className="flex-1 font-medium"
                style={{ color: colors.text }}
              >
                {option.text}
              </Text>

              {showResult && option.is_correct && (
                <Ionicons name="checkmark-circle" size={22} color="#10B981" />
              )}
            </TouchableOpacity>
          ))}
        </View>

        {/* Result Feedback */}
        {showResult && lastAnswerResult && (
          <Animated.View
            className="mt-4 rounded-xl p-4"
            style={{
              backgroundColor: lastAnswerResult.is_correct
                ? "#10B98120"
                : "#EF444420",
              opacity: resultAnim,
              transform: [
                {
                  scale: resultAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.9, 1],
                  }),
                },
              ],
            }}
          >
            <View className="flex-row items-center mb-2">
              <Ionicons
                name={lastAnswerResult.is_correct ? "checkmark-circle" : "close-circle"}
                size={24}
                color={lastAnswerResult.is_correct ? "#10B981" : "#EF4444"}
              />
              <Text
                className="ml-2 text-lg font-bold"
                style={{
                  color: lastAnswerResult.is_correct ? "#10B981" : "#EF4444",
                }}
              >
                {lastAnswerResult.is_correct ? "Correct!" : "Helaas!"}
              </Text>
              {lastAnswerResult.points_earned > 0 && (
                <View
                  className="ml-auto px-3 py-1 rounded-full"
                  style={{ backgroundColor: "#F59E0B" }}
                >
                  <Text className="text-sm font-bold text-white">
                    +{lastAnswerResult.points_earned}
                  </Text>
                </View>
              )}
            </View>

            {lastAnswerResult.explanation && (
              <Text style={{ color: colors.textSecondary }}>
                {lastAnswerResult.explanation}
              </Text>
            )}
          </Animated.View>
        )}
      </Animated.View>

      {/* Next Button */}
      {showResult && (
        <View className="px-4 pb-4">
          <TouchableOpacity
            onPress={handleNextQuestion}
            className="rounded-2xl py-4 flex-row items-center justify-center"
            style={{ backgroundColor: colors.primary }}
            accessibilityRole="button"
            accessibilityLabel={
              currentIndex < totalQuestions - 1 ? "Volgende vraag" : "Resultaten bekijken"
            }
          >
            <Text className="text-lg font-bold text-white">
              {currentIndex < totalQuestions - 1 ? "Volgende Vraag" : "Bekijk Resultaten"}
            </Text>
            <Ionicons name="arrow-forward" size={22} color="#FFFFFF" className="ml-2" />
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
}

